import { RefObject } from 'react';
import { Toast } from 'primereact/toast';

/**
 * Generates a shareable URL for a product
 * @param productId - The ID of the product to share
 * @returns The complete shareable URL
 */
export const generateProductShareUrl = (productId: number): string => {
    return `${window.location.origin}${window.location.pathname}?productId=${productId}`;
};

/**
 * Copies text to clipboard and shows a toast notification
 * @param text - The text to copy
 * @param toast - Reference to the Toast component
 * @returns Promise that resolves when copy is complete
 */
export const copyToClipboard = async (
    text: string,
    toast: RefObject<Toast>
): Promise<void> => {
    try {
        await navigator.clipboard.writeText(text);
        toast.current?.show({
            severity: 'success',
            summary: 'Link Copied!',
            detail: 'Product link copied to clipboard',
            life: 3000
        });
    } catch (err) {
        console.error('Failed to copy link:', err);
        toast.current?.show({
            severity: 'error',
            summary: 'Copy Failed',
            detail: 'Failed to copy link to clipboard',
            life: 3000
        });
    }
};

/**
 * Shares a product by copying its URL to clipboard
 * @param productId - The ID of the product to share
 * @param toast - Reference to the Toast component
 */
export const shareProduct = async (
    productId: number,
    toast: RefObject<Toast>
): Promise<void> => {
    const shareUrl = generateProductShareUrl(productId);
    await copyToClipboard(shareUrl, toast);
};
